import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import Login from './components/Login/Login';
import Signup from './components/Signup/Signup';
import Landing from './components/Landing/Landing';
import ProtectedLayout from './components/ProtectedLayout/ProtectedLayout';
import UploadForm from './components/UploadForm/UploadForm';
import MyReports from './components/MyReports/MyReports';
import Account from './components/Account/Account';
import ReportDetails from './components/ReportDetails/ReportDetails';
import ToastContainer from './components/Toast/ToastContainer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { getReturnPath } from './utils/returnPath';

// Create query client outside component to avoid recreation on re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnMount: true,
    },
  },
});

const AppContent: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (email: string, password: string, rememberMe?: boolean) => {
    // Let the rejection propagate to Login.handleSubmit's catch so the error
    // banner renders. navigate() only runs after a resolved login. The
    // NEW_PASSWORD_REQUIRED sentinel also propagates; Login suppresses its
    // banner and needsNewPassword state drives the challenge UI. So does
    // CONFIRM_SIGN_UP_REQUIRED: Login suppresses it too and shows the confirm step.
    // And PASSWORD_RESET_REQUIRED: Login suppresses it and shows the reset-code step.
    // login() never resolves without a signed-in user: any other step rejects
    // with a contact-support error, so navigate() cannot bounce back here.
    // The destination is the page ProtectedRoute sent the user away from, when
    // it is a same-origin path; /reports otherwise (utils/returnPath).
    await login(email, password, rememberMe);
    navigate(getReturnPath(location.state));
  };

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reports" element={<ProtectedLayout><MyReports /></ProtectedLayout>} />
        <Route path="/upload" element={<ProtectedLayout><UploadForm /></ProtectedLayout>} />
        <Route path="/report/:id" element={<ProtectedLayout><ReportDetails /></ProtectedLayout>} />
        <Route path="/account" element={<ProtectedLayout><Account /></ProtectedLayout>} />
        <Route path="/" element={<Landing />} />
      </Routes>
      <ToastContainer />
    </>
  );
};

// Simple fallback for catastrophic errors (outside Router context)
const CatastrophicErrorFallback = (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '2rem',
    fontFamily: 'Roboto, sans-serif',
  }}>
    <div style={{
      background: 'white',
      borderRadius: '1rem',
      padding: '3rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      maxWidth: '500px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>&#9888;</div>
      <h2 style={{ fontFamily: 'Montserrat, sans-serif', color: '#202124' }}>
        Application Error
      </h2>
      <p style={{ color: '#5F6368', marginBottom: '1.5rem' }}>
        Something went wrong loading the application. Please refresh the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#1A73E8',
          color: 'white',
          border: 'none',
          padding: '0.75rem 1.5rem',
          borderRadius: '0.5rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Refresh Page
      </button>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary fallback={CatastrophicErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <Router>
              <AppContent />
            </Router>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
